import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Search,
  MapPin,
  Home,
  Key,
  Hotel,
  ShieldCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ArrowRight,
  X,
  Calendar,
  Building2,
  LandPlot,
  Check,
  Compass,
  Globe2,
  Filter
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { ALL_CAMEROON_TOWNS, CAMEROON_REGIONS } from '../data/cameroonCities';
import { getLocalizedEquivalentUrl, buildCityUrl, slugify } from '../lib/routes';
import { Property } from '../types';
import { isDemoProperty } from '../lib/demoPropertyHelper';
import { isListingPublic } from '../lib/listingStatusHelper';
import { sanitizeSearchTerm } from '../lib/sanitize';

type SearchTab = 'buy' | 'rent' | 'stay' | 'verify';

interface ModernHeroSearchBarProps {
  className?: string;
  selectedCity?: string;
  onCitySelected?: (city: string) => void;
  properties?: Property[];
}

// 16 Flagship hubs representing all 10 regions in economic priority order
const FLAGSHIP_HUBS = [
  // 1. Centre
  { name: 'Yaoundé', region: 'Centre', icon: '🏛️', topQuarters: ['Bastos', 'Odza', 'Santa Barbara', 'Ngousso', 'Mvan'] },
  // 2. Littoral
  { name: 'Douala', region: 'Littoral', icon: '🏙️', topQuarters: ['Bonapriso', 'Akwa', 'Bonamoussadi', 'Makepe', 'Denver'] },
  { name: 'Edéa', region: 'Littoral', icon: '⚡', topQuarters: ['Pongo', 'Ekite', 'Digue'] },
  // 3. South-West
  { name: 'Buea', region: 'South-West', icon: '⛰️', topQuarters: ['Clerks Quarters', 'Molyko', 'Bokwango', 'Great Soppo'] },
  { name: 'Limbe', region: 'South-West', icon: '🌊', topQuarters: ['Down Beach', 'Bota', 'Ngeme', 'Mile 4'] },
  { name: 'Kumba', region: 'South-West', icon: '🏬', topQuarters: ['Fiango', 'Kumba Town', 'Mambanda'] },
  // 4. South
  { name: 'Kribi', region: 'South', icon: '🏖️', topQuarters: ['Bord de Mer', 'Mpalla', 'Londji', 'Dombe'] },
  { name: 'Ebolowa', region: 'South', icon: '🍃', topQuarters: ['Nkoovos', 'Angale', 'Mekalat'] },
  // 5. West
  { name: 'Bafoussam', region: 'West', icon: '🌿', topQuarters: ['Djeleng', 'Tamda', 'Banengo'] },
  { name: 'Dschang', region: 'West', icon: '🎓', topQuarters: ['Foréké', 'Foto', 'Pays-Bas'] },
  { name: 'Foumban', region: 'West', icon: '🎨', topQuarters: ['Palais', 'Kounga', 'Njinka'] },
  // 6. North-West
  { name: 'Bamenda', region: 'North-West', icon: '🏔️', topQuarters: ['Up Station', 'Commercial Ave', 'Mile 2', 'Nkwen'] },
  // 7. Adamawa
  { name: 'Ngaoundéré', region: 'Adamawa', icon: '🌾', topQuarters: ['Baladji', 'Bambao', 'Sabongari'] },
  // 8. North
  { name: 'Garoua', region: 'North', icon: '☀️', topQuarters: ['Marouaré', 'Poumpoumré', 'Bibémiré'] },
  // 9. Far North
  { name: 'Maroua', region: 'Far North', icon: '🌴', topQuarters: ['Djidjiré', 'Domayo', 'Hardé'] },
  // 10. East
  { name: 'Bertoua', region: 'East', icon: '🌳', topQuarters: ['Mokolo', 'Kano', 'Nkolbikon'] },
];

export const ModernHeroSearchBar: React.FC<ModernHeroSearchBarProps> = ({
  className = '',
  selectedCity,
  onCitySelected,
  properties = [],
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isFr = language === 'fr';

  // Dynamic live listing count calculation per city (social proof & active inventory)
  const { cityListingCounts, totalLiveListings } = useMemo(() => {
    const counts: Record<string, number> = {};
    let total = 0;
    (properties || []).forEach((p) => {
      if (!isDemoProperty(p) && isListingPublic(p)) {
        total += 1;
        const loc = `${p.city || ''} ${p.location || ''}`.toLowerCase();
        FLAGSHIP_HUBS.forEach((hub) => {
          if (loc.includes(hub.name.toLowerCase()) || loc.includes(slugify(hub.name))) {
            counts[hub.name.toLowerCase()] = (counts[hub.name.toLowerCase()] || 0) + 1;
          }
        });
      }
    });
    return { cityListingCounts: counts, totalLiveListings: total };
  }, [properties]);

  const [activeTab, setActiveTab] = useState<SearchTab>('buy');
  const [locationQuery, setLocationQuery] = useState(selectedCity || '');
  const [activeCityHub, setActiveCityHub] = useState<string>(selectedCity || '');
  const [showAllTownsModal, setShowAllTownsModal] = useState(false);
  const [propertyType, setPropertyType] = useState('all');
  const [budgetRange, setBudgetRange] = useState('all');
  const [areaRange, setAreaRange] = useState('all');
  const [titleDeedOnly, setTitleDeedOnly] = useState(false);
  const [titleNumber, setTitleNumber] = useState('');

  // Short stay dates & guests
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState('2');

  // Visual highlight pulse when city is auto-filled
  const [isAutoFilled, setIsAutoFilled] = useState(false);

  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [modalTownSearch, setModalTownSearch] = useState('');
  const locationContainerRef = useRef<HTMLDivElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);

  // Sync when selectedCity prop changes from outside
  useEffect(() => {
    if (selectedCity && selectedCity !== locationQuery) {
      applyCity(selectedCity);
    }
  }, [selectedCity]);

  const applyCity = (cityName: string, quarterName?: string) => {
    const fullVal = quarterName ? `${quarterName}, ${cityName}` : cityName;
    setLocationQuery(fullVal);
    setActiveCityHub(cityName);
    setIsAutoFilled(true);
    setShowLocationDropdown(false);
    setShowAllTownsModal(false);
    onCitySelected?.(fullVal);

    // Focus input so user sees the cursor in the search bar immediately
    setTimeout(() => {
      locationInputRef.current?.focus();
    }, 80);

    // Trigger visual highlight pulse
    setTimeout(() => {
      setIsAutoFilled(false);
    }, 1500);
  };

  // Close location suggestions on click outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (locationContainerRef.current && !locationContainerRef.current.contains(e.target as Node)) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Filter towns & quarters for autocomplete
  const filteredSuggestions = useMemo(() => {
    const q = locationQuery.trim().toLowerCase();
    if (!q) {
      return FLAGSHIP_HUBS.map((h) => ({
        city: h.name,
        region: h.region,
        quarter: h.topQuarters[0],
      }));
    }

    const matches: Array<{ city: string; region: string; quarter?: string }> = [];
    for (const town of ALL_CAMEROON_TOWNS) {
      if (town.name.toLowerCase().includes(q)) {
        matches.push({ city: town.name, region: town.region });
      }
      for (const qr of town.popularQuarters) {
        if (qr.toLowerCase().includes(q)) {
          matches.push({ city: town.name, region: town.region, quarter: qr });
        }
      }
      if (matches.length >= 10) break;
    }
    return matches.slice(0, 10);
  }, [locationQuery]);

  // Generate dedicated, SEO-optimized URL for any city & tab
  const getCityDestinationUrl = useCallback((cityName: string, quarterName?: string): string => {
    const citySlug = slugify(cityName);
    if (!citySlug) return isFr ? '/fr/villes' : '/en/cities';

    if (activeTab === 'verify') {
      const verifyBase = isFr ? '/fr/verifier-propriete' : '/en/verify-property';
      return `${verifyBase}?city=${encodeURIComponent(cityName)}`;
    }

    if (activeTab === 'stay') {
      const base = isFr ? `/fr/courts-sejours/${citySlug}` : `/en/short-stays/${citySlug}`;
      return base;
    }

    if (activeTab === 'rent') {
      let base = isFr ? `/fr/biens-a-louer/${citySlug}` : `/en/properties-for-rent/${citySlug}`;
      if (propertyType === 'apartment') base = isFr ? `/fr/appartements-a-louer/${citySlug}` : `/en/apartments-for-rent/${citySlug}`;
      else if (propertyType === 'house') base = isFr ? `/fr/maisons-a-louer/${citySlug}` : `/en/houses-for-rent/${citySlug}`;
      else if (propertyType === 'studio') base = isFr ? `/fr/studios-a-louer/${citySlug}` : `/en/studios-for-rent/${citySlug}`;
      else if (propertyType === 'room') base = isFr ? `/fr/chambres-a-louer/${citySlug}` : `/en/rooms-for-rent/${citySlug}`;
      else if (propertyType === 'land') base = isFr ? `/fr/terrains-a-louer/${citySlug}` : `/en/land-for-rent/${citySlug}`;
      else if (propertyType === 'commercial') base = isFr ? `/fr/locaux-commerciaux-a-louer/${citySlug}` : `/en/commercial-for-rent/${citySlug}`;

      if (quarterName) {
        return `${base}?quarter=${encodeURIComponent(quarterName)}`;
      }
      return base;
    }

    // Default: 'buy'
    let base = isFr ? `/fr/biens-a-vendre/${citySlug}` : `/en/properties-for-sale/${citySlug}`;
    if (propertyType === 'land') base = isFr ? `/fr/terrains-a-vendre/${citySlug}` : `/en/land-for-sale/${citySlug}`;
    else if (propertyType === 'house') base = isFr ? `/fr/maisons-a-vendre/${citySlug}` : `/en/houses-for-sale/${citySlug}`;
    else if (propertyType === 'apartment') base = isFr ? `/fr/appartements-a-vendre/${citySlug}` : `/en/apartments-for-sale/${citySlug}`;
    else if (propertyType === 'commercial') base = isFr ? `/fr/immobilier-commercial-a-vendre/${citySlug}` : `/en/commercial-for-sale/${citySlug}`;

    if (quarterName) {
      return `${base}?quarter=${encodeURIComponent(quarterName)}`;
    }
    return base;
  }, [activeTab, propertyType, isFr]);

  const handleExecuteSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (activeTab === 'verify') {
      const verifyRoute = isFr ? '/fr/verifier-propriete' : '/en/verify-property';
      const params = new URLSearchParams();
      const safeTitleNumber = sanitizeSearchTerm(titleNumber, 80);
      if (safeTitleNumber) {
        params.set('titleNumber', safeTitleNumber);
      }
      navigate(`${verifyRoute}${params.toString() ? `?${params.toString()}` : ''}`);
      return;
    }

    if (activeTab === 'stay') {
      const stayRoute = isFr ? '/fr/residences-meublees' : '/en/short-stays';
      const params = new URLSearchParams();
      const safeCity = sanitizeSearchTerm(locationQuery, 80);
      if (safeCity) params.set('city', safeCity);
      if (checkIn) params.set('checkin', sanitizeSearchTerm(checkIn, 20));
      if (checkOut) params.set('checkout', sanitizeSearchTerm(checkOut, 20));
      if (guests) params.set('guests', sanitizeSearchTerm(guests, 10));
      navigate(`${stayRoute}${params.toString() ? `?${params.toString()}` : ''}`);
      return;
    }

    // Buy or Rent
    let baseCategory = 'properties-for-sale';
    if (activeTab === 'rent') {
      baseCategory = 'properties-for-rent';
      if (propertyType === 'apartment') baseCategory = 'apartments-for-rent';
      else if (propertyType === 'house') baseCategory = 'houses-for-rent';
      else if (propertyType === 'studio') baseCategory = 'studios-for-rent';
      else if (propertyType === 'room') baseCategory = 'rooms-for-rent';
      else if (propertyType === 'commercial') baseCategory = 'commercial-for-rent';
      else if (propertyType === 'land') baseCategory = 'land-for-rent';
    } else {
      // Buy
      if (propertyType === 'land') baseCategory = 'land-for-sale';
      else if (propertyType === 'house') baseCategory = 'houses-for-sale';
      else if (propertyType === 'apartment') baseCategory = 'apartments-for-sale';
      else if (propertyType === 'commercial') baseCategory = 'commercial-for-sale';
    }

    // Resolve City slug if user selected or typed a location
    let targetCitySlug = '';
    let quarterParam = '';
    const safeLocQuery = sanitizeSearchTerm(locationQuery, 100);
    if (safeLocQuery) {
      const parts = safeLocQuery.split(',').map((s) => s.trim());
      if (parts.length > 1) {
        quarterParam = sanitizeSearchTerm(parts[0], 80);
        targetCitySlug = slugify(parts[1] || activeCityHub || '');
      } else {
        targetCitySlug = slugify(parts[0]);
      }
    } else if (activeCityHub) {
      targetCitySlug = slugify(sanitizeSearchTerm(activeCityHub, 50));
    }

    // Build the clean, crawlable canonical route
    let routePath = `/${baseCategory}`;
    if (targetCitySlug) {
      routePath = `/${baseCategory}/${targetCitySlug}`;
    }

    const localizedRoute = getLocalizedEquivalentUrl(routePath, language);
    const params = new URLSearchParams();

    if (quarterParam) {
      params.set('quarter', quarterParam);
    }
    if (budgetRange !== 'all') {
      params.set('budget', budgetRange);
    }
    if (areaRange !== 'all') {
      params.set('area', areaRange);
    }
    if (titleDeedOnly) {
      params.set('titre_foncier', 'true');
    }

    const queryStr = params.toString();
    navigate(queryStr ? `${localizedRoute}?${queryStr}` : localizedRoute);
  };

  return (
    <div className={`w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-30 ${className}`}>
      {/* Outer Card with Crisp White Surface & Elevated Slate Shadow */}
      <div className="bg-white shadow-[0_20px_50px_-12px_rgba(15,23,42,0.16),0_1px_3px_rgba(15,23,42,0.06)] rounded-3xl sm:rounded-[28px] border border-slate-200/90 p-4 sm:p-6 lg:p-7 transition-all">
        
        {/* Top Header: Purpose Switcher (Tabs) + Cadastre Trust Pill */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pb-3.5 border-b border-slate-200/80">
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar py-0.5">
            <button
              type="button"
              onClick={() => setActiveTab('buy')}
              className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'buy'
                  ? 'bg-[#094426] text-white shadow-sm'
                  : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
              }`}
            >
              <Home className={`w-4 h-4 ${activeTab === 'buy' ? 'text-emerald-300' : 'text-slate-500'}`} />
              <span>{isFr ? 'Acheter' : 'Buy'}</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('rent')}
              className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'rent'
                  ? 'bg-[#094426] text-white shadow-sm'
                  : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
              }`}
            >
              <Key className={`w-4 h-4 ${activeTab === 'rent' ? 'text-emerald-300' : 'text-slate-500'}`} />
              <span>{isFr ? 'Louer' : 'Rent'}</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('stay')}
              className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'stay'
                  ? 'bg-[#094426] text-white shadow-sm'
                  : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
              }`}
            >
              <Hotel className={`w-4 h-4 ${activeTab === 'stay' ? 'text-emerald-300' : 'text-slate-500'}`} />
              <span>{isFr ? 'Courts Séjours' : 'Short Stays'}</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('verify')}
              className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'verify'
                  ? 'bg-[#b45309] text-white shadow-sm'
                  : 'text-amber-900 bg-amber-50 hover:bg-amber-100/80 border border-amber-300/80'
              }`}
            >
              <ShieldCheck className={`w-4 h-4 ${activeTab === 'verify' ? 'text-amber-200' : 'text-amber-600'}`} />
              <span>{isFr ? 'Vérifier Titre Foncier' : 'Verify Title Deed'}</span>
            </button>
          </div>

          <div className="hidden lg:flex items-center gap-2 text-xs font-semibold text-[#094426] bg-emerald-50/90 px-3 py-1.5 rounded-xl border border-emerald-200/70">
            <Globe2 className="w-3.5 h-3.5 text-[#094426]" />
            <span>
              {isFr
                ? `${totalLiveListings > 0 ? `${totalLiveListings} ${totalLiveListings === 1 ? 'Annonce Vérifiée' : 'Annonces Vérifiées'} • ` : ''}10 Régions • 58 Départements • ${ALL_CAMEROON_TOWNS.length} Villes`
                : `${totalLiveListings > 0 ? `${totalLiveListings} ${totalLiveListings === 1 ? 'Verified Ad' : 'Verified Ads'} • ` : ''}All 10 Regions • 58 Divisions • ${ALL_CAMEROON_TOWNS.length} Towns`}
            </span>
          </div>
        </div>

        {/* Mobile Active Ads Count Badge */}
        {totalLiveListings > 0 && (
          <div className="lg:hidden flex items-center gap-1.5 text-[11px] font-bold text-[#094426] bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200/60 self-start mt-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>
              {totalLiveListings} {isFr ? (totalLiveListings === 1 ? 'Annonce active en ligne' : 'Annonces actives en ligne') : (totalLiveListings === 1 ? 'Live active ad' : 'Live active ads')}
            </span>
          </div>
        )}

        {/* 2. Main Search Bar Input Fields */}
        <form onSubmit={handleExecuteSearch} className="mt-1 pt-3.5">
          {activeTab === 'verify' ? (
            /* MINDCAF Titre Foncier Quick Audit Bar */
            <div className="flex flex-col md:flex-row items-center gap-3">
              <div className="relative flex-1 w-full">
                <ShieldCheck className="w-5 h-5 text-amber-600 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={titleNumber}
                  onChange={(e) => setTitleNumber(e.target.value)}
                  placeholder={
                    isFr
                      ? 'Entrez le N° de Titre Foncier (ex: 12450/Mfoundi, 8792/Wouri, 3421/Fako)...'
                      : 'Enter Cameroon Land Title Deed Number (e.g. 12450/Mfoundi, 8792/Wouri)...'
                  }
                  className="w-full pl-12 pr-4 py-3.5 bg-amber-50/70 border border-amber-300/80 rounded-2xl text-xs sm:text-sm font-semibold text-stone-900 placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white"
                />
              </div>
              <button
                type="submit"
                className="w-full md:w-auto px-8 py-3.5 bg-[#a35b04] hover:bg-[#874903] text-white font-extrabold text-xs sm:text-sm rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer shrink-0"
              >
                <Search className="w-4 h-4" />
                <span>{isFr ? 'Contrôler au Cadastre' : 'Check Title Registry'}</span>
              </button>
            </div>
          ) : activeTab === 'stay' ? (
            /* Short Stay Booking Bar */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-center">
              {/* Destination */}
              <div className="lg:col-span-4 relative" ref={locationContainerRef}>
                <label className="block text-[11px] font-bold text-stone-700 mb-1">
                  {isFr ? 'Destination au Cameroun' : 'Destination in Cameroon'}
                </label>
                <div className="relative">
                  <MapPin className="w-4 h-4 text-emerald-700 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    ref={locationInputRef}
                    type="text"
                    value={locationQuery}
                    onChange={(e) => {
                      setLocationQuery(e.target.value);
                      setShowLocationDropdown(true);
                    }}
                    onFocus={() => setShowLocationDropdown(true)}
                    placeholder={isFr ? 'Ville (Kribi, Douala, Yaoundé, Limbe...)' : 'City (Kribi, Douala, Yaoundé, Limbe...)'}
                    className={`w-full pl-10 pr-8 py-3 bg-white hover:bg-stone-50 focus:bg-white border border-stone-300 rounded-2xl text-xs sm:text-sm font-bold text-stone-950 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#063b22] transition-all ${
                      isAutoFilled ? 'ring-2 ring-emerald-600 bg-emerald-50/80 scale-[1.01]' : ''
                    }`}
                  />
                  {locationQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setLocationQuery('');
                        setActiveCityHub('');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Autocomplete */}
                {showLocationDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-stone-200 rounded-2xl shadow-2xl z-50 max-h-60 overflow-y-auto py-2">
                    {filteredSuggestions.map((loc, i) => (
                      <button
                        key={`${loc.city}-${loc.quarter || i}`}
                        type="button"
                        onClick={() => applyCity(loc.city, loc.quarter)}
                        className="w-full text-left px-4 py-2 hover:bg-emerald-50/80 text-xs flex items-center justify-between group cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-emerald-700" />
                          <span className="font-bold text-stone-800">
                            {loc.quarter ? `${loc.quarter}, ${loc.city}` : loc.city}
                          </span>
                        </div>
                        <span className="text-[10px] text-stone-400">{loc.region}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Check-in */}
              <div className="lg:col-span-3">
                <label className="block text-[11px] font-bold text-stone-700 mb-1">
                  {isFr ? 'Arrivée' : 'Check-in'}
                </label>
                <div className="relative">
                  <Calendar className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="date"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    className="w-full pl-9 pr-3 py-3 bg-white hover:bg-stone-50 focus:bg-white border border-stone-300 rounded-2xl text-xs sm:text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#063b22]"
                  />
                </div>
              </div>

              {/* Check-out */}
              <div className="lg:col-span-3">
                <label className="block text-[11px] font-bold text-stone-700 mb-1">
                  {isFr ? 'Départ' : 'Check-out'}
                </label>
                <div className="relative">
                  <Calendar className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="date"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    className="w-full pl-9 pr-3 py-3 bg-white hover:bg-stone-50 focus:bg-white border border-stone-300 rounded-2xl text-xs sm:text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#063b22]"
                  />
                </div>
              </div>

              {/* Search CTA */}
              <div className="lg:col-span-2 pt-3 sm:pt-0 self-end">
                <button
                  type="submit"
                  className="w-full py-3.5 bg-[#094426] hover:bg-[#07361e] text-white font-bold text-xs sm:text-sm rounded-2xl shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
                >
                  <Search className="w-4 h-4" />
                  <span>{isFr ? 'Explorer' : 'Search'}</span>
                </button>
              </div>
            </div>
          ) : (
            /* Buy / Rent Main Search Bar */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-center">
              
              {/* Location with Smart Autocomplete and Auto-fill Highlight */}
              <div className="lg:col-span-4 relative" ref={locationContainerRef}>
                <label className="block text-[11px] font-bold text-stone-700 mb-1">
                  {isFr ? 'Localisation au Cameroun (Ville ou Quartier)' : 'Location in Cameroon (City or Quarter)'}
                </label>
                <div className="relative">
                  <MapPin className="w-4 h-4 text-emerald-700 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    ref={locationInputRef}
                    type="text"
                    value={locationQuery}
                    onChange={(e) => {
                      setLocationQuery(e.target.value);
                      setShowLocationDropdown(true);
                    }}
                    onFocus={() => setShowLocationDropdown(true)}
                    placeholder={
                      isFr
                        ? 'Ex: Yaoundé, Douala, Kribi, Limbe, Bamenda, Bastos...'
                        : 'e.g. Yaoundé, Douala, Kribi, Limbe, Bamenda, Bastos...'
                    }
                    className={`w-full pl-10 pr-8 py-3 bg-white hover:bg-stone-50 focus:bg-white border border-stone-300 rounded-2xl text-xs sm:text-sm font-bold text-stone-950 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#063b22] transition-all duration-300 ${
                      isAutoFilled
                        ? 'ring-2 ring-emerald-600 bg-emerald-50/80 scale-[1.01] text-emerald-950'
                        : ''
                    }`}
                  />
                  {locationQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setLocationQuery('');
                        setActiveCityHub('');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Dropdown Suggestions */}
                {showLocationDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-stone-200 rounded-2xl shadow-2xl z-50 max-h-64 overflow-y-auto py-2">
                    <div className="px-3.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-stone-400">
                      {isFr ? 'Villes & Quartiers Populaires' : 'Popular Cities & Quarters'}
                    </div>
                    {filteredSuggestions.map((loc, i) => {
                      const count = cityListingCounts[loc.city.toLowerCase()] || 0;
                      return (
                        <button
                          key={`${loc.city}-${loc.quarter || i}`}
                          type="button"
                          onClick={() => applyCity(loc.city, loc.quarter)}
                          className="w-full text-left px-4 py-2 hover:bg-emerald-50/80 text-xs flex items-center justify-between group cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 text-emerald-700" />
                            <span className="font-bold text-stone-800">
                              {loc.quarter ? `${loc.quarter}, ${loc.city}` : loc.city}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {count > 0 && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-[#094426] border border-emerald-200">
                                {count} {isFr ? (count === 1 ? 'annonce' : 'annonces') : (count === 1 ? 'ad' : 'ads')}
                              </span>
                            )}
                            <span className="text-[10px] text-stone-400 font-medium">{loc.region}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Property Type */}
              <div className="lg:col-span-2">
                <label className="block text-[11px] font-bold text-stone-700 mb-1">
                  {isFr ? 'Type de Propriété' : 'Property Type'}
                </label>
                <div className="relative">
                  <select
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)}
                    className="w-full px-3 py-3 bg-white hover:bg-stone-50 focus:bg-white border border-stone-300 rounded-2xl text-xs sm:text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#063b22] appearance-none cursor-pointer"
                  >
                    <option value="all">{isFr ? 'Tous les types' : 'All Types'}</option>
                    <option value="land">{isFr ? 'Terrain / Parcelle' : 'Land / Plot'}</option>
                    <option value="house">{isFr ? 'Villa / Maison' : 'Villa / House'}</option>
                    <option value="apartment">{isFr ? 'Appartement' : 'Apartment'}</option>
                    <option value="commercial">{isFr ? 'Boutiques & Bureaux' : 'Shops & Offices'}</option>
                    {activeTab === 'rent' && (
                      <>
                        <option value="studio">{isFr ? 'Studio moderne' : 'Modern Studio'}</option>
                        <option value="room">{isFr ? 'Chambre' : 'Room'}</option>
                      </>
                    )}
                  </select>
                  <ChevronDown className="w-4 h-4 text-stone-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              {/* Budget Filter */}
              <div className="lg:col-span-2">
                <label className="block text-[11px] font-bold text-stone-700 mb-1">
                  {isFr ? 'Budget (XAF)' : 'Budget (XAF)'}
                </label>
                <div className="relative">
                  <select
                    value={budgetRange}
                    onChange={(e) => setBudgetRange(e.target.value)}
                    className="w-full px-3 py-3 bg-white hover:bg-stone-50 focus:bg-white border border-stone-300 rounded-2xl text-xs sm:text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#063b22] appearance-none cursor-pointer"
                  >
                    <option value="all">{isFr ? 'Tous budgets' : 'Any Budget'}</option>
                    {activeTab === 'rent' ? (
                      <>
                        <option value="0-50000">&lt; 50k FCFA</option>
                        <option value="50000-150000">50k - 150k FCFA</option>
                        <option value="150000-300000">150k - 300k FCFA</option>
                        <option value="300000+">&gt; 300k FCFA</option>
                      </>
                    ) : (
                      <>
                        <option value="under-5m">{isFr ? '< 5M FCFA' : '< 5M XAF'}</option>
                        <option value="5m-15m">5M - 15M FCFA</option>
                        <option value="15m-30m">15M - 30M FCFA</option>
                        <option value="30m-60m">30M - 60M FCFA</option>
                        <option value="60m-120m">60M - 120M FCFA</option>
                        <option value="over-120m">{isFr ? '> 120M FCFA' : '> 120M XAF'}</option>
                      </>
                    )}
                  </select>
                  <ChevronDown className="w-4 h-4 text-stone-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              {/* Surface Area Filter (m²) */}
              <div className="lg:col-span-2">
                <label className="block text-[11px] font-bold text-stone-700 mb-1">
                  {isFr ? 'Superficie (m²)' : 'Area (m²)'}
                </label>
                <div className="relative">
                  <select
                    value={areaRange}
                    onChange={(e) => setAreaRange(e.target.value)}
                    className="w-full px-3 py-3 bg-white hover:bg-stone-50 focus:bg-white border border-stone-300 rounded-2xl text-xs sm:text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#063b22] appearance-none cursor-pointer"
                  >
                    <option value="all">{isFr ? 'Toutes superficies' : 'All Sizes'}</option>
                    <option value="under-300">&lt; 300 m²</option>
                    <option value="300-600">300 - 600 m²</option>
                    <option value="600-1500">600 - 1 500 m²</option>
                    <option value="1500-5000">1 500 - 5 000 m²</option>
                    <option value="over-5000">&gt; 5 000 m²</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-stone-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              {/* Search Button */}
              <div className="lg:col-span-2 pt-3 sm:pt-0 self-end">
                <button
                  type="submit"
                  className="w-full py-3.5 bg-[#094426] hover:bg-[#07361e] text-white font-bold text-xs sm:text-sm rounded-2xl shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
                >
                  <Search className="w-4 h-4" />
                  <span>{isFr ? 'Rechercher' : 'Search'}</span>
                </button>
              </div>

              {/* Titre Foncier Quick Toggle Bar */}
              <div className="lg:col-span-12 flex flex-wrap items-center justify-between gap-2 pt-2.5 mt-0.5 border-t border-stone-200/60">
                <button
                  type="button"
                  onClick={() => setTitleDeedOnly((prev) => !prev)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    titleDeedOnly
                      ? 'bg-[#063b22] text-white shadow-xs ring-2 ring-emerald-500'
                      : 'bg-emerald-50 text-[#063b22] border border-emerald-200 hover:bg-emerald-100'
                  }`}
                >
                  <ShieldCheck className={`w-4 h-4 ${titleDeedOnly ? 'text-amber-300' : 'text-emerald-700'}`} />
                  <span>{isFr ? 'Titre Foncier Vérifié Uniquement (MINDCAF)' : 'Verified Title Deed (Titre Foncier) Only'}</span>
                  {titleDeedOnly && <Check className="w-3.5 h-3.5 text-emerald-300 ml-1" />}
                </button>

                {(locationQuery || propertyType !== 'all' || budgetRange !== 'all' || areaRange !== 'all' || titleDeedOnly) && (
                  <button
                    type="button"
                    onClick={() => {
                      setLocationQuery('');
                      setActiveCityHub('');
                      setPropertyType('all');
                      setBudgetRange('all');
                      setAreaRange('all');
                      setTitleDeedOnly(false);
                    }}
                    className="text-[11px] font-semibold text-stone-500 hover:text-stone-800 underline cursor-pointer"
                  >
                    {isFr ? 'Réinitialiser la recherche' : 'Reset search'}
                  </button>
                )}
              </div>
            </div>
          )}
        </form>
      </div>

      {/* 4. Modal: Browse All Cameroon Towns Across 10 Regions */}
      {showAllTownsModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[85vh] flex flex-col shadow-2xl border border-emerald-900/20 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between bg-[#fcfbf9]">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-800 font-bold">
                  🇨🇲
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-extrabold text-stone-900">
                    {isFr ? `Répertoire des ${ALL_CAMEROON_TOWNS.length} Villes du Cameroun` : `All ${ALL_CAMEROON_TOWNS.length} Cameroon Cities & Towns`}
                  </h3>
                  <p className="text-xs text-stone-500">
                    {isFr
                      ? 'Cliquez sur n’importe quelle ville pour l’insérer instantanément dans la barre de recherche'
                      : 'Click any city to auto-fill it directly into the search bar'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowAllTownsModal(false);
                  setModalTownSearch('');
                }}
                className="p-2 text-stone-400 hover:text-stone-700 rounded-full hover:bg-stone-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Quick Live Search Bar Inside Modal */}
            <div className="px-6 pt-4 pb-2 bg-stone-50/50 border-b border-stone-200/60">
              <div className="relative">
                <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={modalTownSearch}
                  onChange={(e) => setModalTownSearch(e.target.value)}
                  placeholder={
                    isFr
                      ? `Rechercher parmi les ${ALL_CAMEROON_TOWNS.length} villes (ex: Douala, Kribi, Obala)...`
                      : `Search across all ${ALL_CAMEROON_TOWNS.length} towns & cities (e.g. Douala, Kribi, Obala)...`
                  }
                  className="w-full pl-10 pr-9 py-2 bg-white border border-stone-300 rounded-xl text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#063b22]"
                />
                {modalTownSearch && (
                  <button
                    type="button"
                    onClick={() => setModalTownSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700 p-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Modal Body: Grouped by 10 Regions */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              {CAMEROON_REGIONS.map((region) => {
                const searchLower = modalTownSearch.trim().toLowerCase();
                let townsInRegion = ALL_CAMEROON_TOWNS.filter((t) => t.region.toLowerCase() === region.name.toLowerCase());
                if (searchLower) {
                  townsInRegion = townsInRegion.filter(
                    (t) =>
                      t.name.toLowerCase().includes(searchLower) ||
                      t.division.toLowerCase().includes(searchLower) ||
                      t.popularQuarters?.some((q) => q.toLowerCase().includes(searchLower))
                  );
                }

                if (townsInRegion.length === 0) return null;

                return (
                  <div key={region.code} className="space-y-2">
                    <div className="flex items-center gap-2 pb-1 border-b border-stone-200/80">
                      <span className="font-extrabold text-xs uppercase tracking-wider text-emerald-900">
                        {isFr ? `Région du ${region.nameFr || region.name}` : `${region.name} Region`} ({townsInRegion.length} {isFr ? 'villes' : 'towns'})
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {townsInRegion.map((town) => {
                        const townDestUrl = getCityDestinationUrl(town.name);
                        return (
                          <Link
                            key={town.slug}
                            to={townDestUrl}
                            onClick={() => {
                              setShowAllTownsModal(false);
                              setModalTownSearch('');
                            }}
                            className="text-left px-3 py-2 rounded-xl text-xs font-semibold bg-stone-50 hover:bg-emerald-50 hover:text-[#063b22] text-stone-800 border border-stone-200 transition-colors cursor-pointer flex items-center justify-between group"
                          >
                            <span className="truncate group-hover:font-bold">{town.name}</span>
                            <span className="text-[10px] text-stone-400 shrink-0 ml-1">{town.division}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3.5 bg-stone-50 border-t border-stone-200 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowAllTownsModal(false);
                  setModalTownSearch('');
                }}
                className="px-5 py-2 rounded-xl text-xs font-bold text-stone-700 hover:bg-stone-200/70 cursor-pointer"
              >
                {isFr ? 'Fermer' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
